import {DTStoreNode, StoreNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {LocationTargetingMethodValue} from 'common/src/modules/locationFeed/locationFeed.types';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {toAiLocationTargetingMethod} from '../aiModels/cardDetailsAI.enums';
import {
  formatOutputHeader,
  getBaseHeader,
  getDestinationPhrase,
  getOutputTypePhrase,
  sortOutputsByType,
} from './utils';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';

function getLocationOutputWhenPhrase(output: StoreNodeOutputData): string {
  switch (output.type) {
    case OutputType.Regular:
    case OutputType.Last:
      return 'when store selected';
    case OutputType.Fallback:
    default:
      return 'when no store';
  }
}

export function locationCardToText(
  card: DTStoreNode,
  mode: 'short' | 'full',
  fallbackCreativeNodeId?: number,
): string {
  const header = getBaseHeader(card);
  const locationMethodName = toAiLocationTargetingMethod(card.locationTargetingMethod) ?? '';
  const sortedOutputs = sortOutputsByType(card.outputs);

  if (mode === 'short') {
    const purpose = locationMethodName ? `Purpose: Select store by ${locationMethodName}` : '';

    const outputLines = sortedOutputs.map((output: StoreNodeOutputData, index) => {
      const outputHeader = formatOutputHeader(index, output);
      const outputType = getOutputTypePhrase(output);
      const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

      return `${outputHeader} ${outputType} ${dest}`;
    });

    return [header, purpose, ...outputLines].filter(Boolean).join('\n');
  }

  const locationFeedLine = card.locationFeed
    ? `Location Feed '${card.locationFeed.name}' ID ${
        card.locationFeed.id != null ? idHelper.toLocationFeedId(card.locationFeed.id) : 'not set'
      }`
    : 'Location Feed not set';
  const locationSetLine = card.locationSet
    ? `Location Set '${card.locationSet.name}' ID ${
        card.locationSet.id != null ? idHelper.toLocationSetId(card.locationSet.id) : 'not set'
      }`
    : 'Location Set not set';
  const strategyIdLine =
    card.locationTargetingMethod != null
      ? `Strategy ID: ${idHelper.toLocationStrategyId(card.locationTargetingMethod)}`
      : 'Strategy ID: not set';
  const isSpecificStore =
    card.locationTargetingMethod === LocationTargetingMethodValue.SpecificStore;
  const topLocationIdsLine =
    isSpecificStore && card.topLocationIds?.length
      ? `Specific Location Ids [${card.topLocationIds.map((id) => `'${id}'`).join(',')}]`
      : isSpecificStore
        ? 'Specific Location Ids not set'
        : null;

  const outputLines = sortedOutputs.map((output: StoreNodeOutputData, index) => {
    const outputHeader = formatOutputHeader(index, output);
    const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);
    const whenPhrase = getLocationOutputWhenPhrase(output);
    const valuesPart = output.values?.length ? ` values=[${output.values.join(', ')}]` : '';

    return `${outputHeader} ${dest} ${whenPhrase}${valuesPart}`;
  });

  return [header, strategyIdLine, locationFeedLine, locationSetLine, topLocationIdsLine, ...outputLines]
    .filter(Boolean)
    .join('\n');
}
